import isEmpty from "lodash/isEmpty";
import { Option, OptionOriginal } from "PFTypes/options";
import { Fragment, useRef } from "react";

import { SelectProps } from "../../select.types";
import { getIds } from "../../select.utils";
import { ResultOption } from "../types";
import { HighlightedIndex } from "../use_highlighted_index";
import css from "./list.module.scss";
import { getListItemId } from "./list.utils";
import { ListItem } from "./list_item";
import { useAutoScrollToSelected } from "./use_auto_scroll_to_selected";

type ListProps<T> = {
  id: string;
  options: ResultOption<T>[];
  onChange: (option: Option<T>, selected: boolean) => void;
  highlightedIndex: HighlightedIndex;
  highlightOption: (option: Option<T>, options: Option<T>[]) => void;
} & Pick<SelectProps<T>, "value" | "multiple" | "clipOptionsOverflow">;

export const List = <T extends OptionOriginal = OptionOriginal>({
  id,
  value,
  options,
  multiple,
  onChange,
  highlightedIndex,
  highlightOption,
  clipOptionsOverflow
}: ListProps<T>) => {
  const selectedIds = getIds(value);
  const listRef = useRef<HTMLUListElement>(null);
  useAutoScrollToSelected({ listRef, multiple, selectedIds, listId: id });

  if (isEmpty(options)) {
    return null;
  }

  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      className={css.list}
      aria-multiselectable={multiple}
      tabIndex={-1}
    >
      <>
        {(options ?? []).map((option, index) => {
          const selected = selectedIds.includes(option.id);

          return (
            <Fragment key={option.id}>
              <ListItem
                id={getListItemId(id, String(option.id))}
                option={option}
                selected={option.selected ?? selected}
                highlighted={highlightedIndex === index}
                onClick={() => {
                  const handleChange = option.onChange || onChange;
                  handleChange(option, !selected);
                  highlightOption(option, options);
                }}
                multiple={multiple}
                clipOptionsOverflow={clipOptionsOverflow}
              />
            </Fragment>
          );
        })}
      </>
    </ul>
  );
};
